import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { DomSanitizer } from '@angular/platform-browser';
import * as FileSaver from 'file-saver';
import { Observable, Observer } from 'rxjs';
import { BackendResponse, DialogService, HttpConfig, HttpService, TranslateService } from 'vas-mobile-api';

import { ReportFilter, ReportTask } from '../models/report';
import { PdfViewerComponent } from '../pdf-viewer/pdf-viewer.component';

@Injectable({
  providedIn: 'root'
})
export class PrintService {

  constructor
    (private httpService: HttpService,
      private sanitizer: DomSanitizer,
      private dialog: MatDialog,
      private dialogService: DialogService,
      private translateService: TranslateService) { }

  public printDNO(reportId: string, deliveryNo: string, isMobile: boolean): Observable<boolean> {
    const parameters: any = {};
    parameters.reportId = reportId;

    const config: HttpConfig = {
      pathId: 'printDNO',
      urlParams: {
        deliveryNo: deliveryNo,
        reportId: reportId
      }
    };

    return new Observable((observer: Observer<boolean>) => {
      this.httpService.get<BackendResponse<string>>(config).subscribe((response) => {
        if (isMobile) {
          this.renderReportMobile(response.data, ReportTask.PDF, reportId);
        } else {
          this.renderReport(response.data, ReportTask.PDF, reportId);
        }
        observer.next(true);
        observer.complete();
      }, (e) => {
        this.showDialogError();
        observer.next(false);
        observer.complete();
      });
    });
  }

  public printDNOFromArchive(reportId: string, deliveryNo: string, formularCode: string, isMobile: boolean): Observable<boolean> {
    const config: HttpConfig = {
      pathId: 'printDNOFromArchive',
      urlParams: {
        deliveryNo: deliveryNo,
        reportId: reportId,
        formularCode: formularCode
      }
    };

    return new Observable((observer: Observer<boolean>) => {
      this.httpService.get<BackendResponse<string>>(config).subscribe((response) => {
        if (isMobile) {
          this.renderReportMobile(response.data, ReportTask.PDF, reportId);
        } else {
          this.renderReport(response.data, ReportTask.PDF, reportId);
        }
        observer.next(true);
        observer.complete();
      }, (e) => {
        this.showDialogError();
        observer.next(false);
        observer.complete();
      });
    });
  }

  public printReport(
    reportId: string, reportFileName: string, task: ReportTask, filters: ReportFilter[], isMobile: boolean
  ): Observable<boolean> {

    const parameters: any = {};
    parameters.reportId = reportId;
    parameters.reportFileName = reportFileName;
    parameters.task = task;

    const config: HttpConfig = {
      pathId: 'printReport',
      payload: filters,
      urlParams: parameters,
      customTimeout: 300000
    };

    return new Observable((observer: Observer<boolean>) => {
      this.httpService.post<BackendResponse<string>>(config).subscribe((response) => {
        if (isMobile) {
          this.renderReportMobile(response.data, task, (reportFileName || reportId));
        } else {
          this.renderReport(response.data, task, (reportFileName || reportId));
        }
        observer.next(true);
        observer.complete();
      }, (e) => {
        this.showDialogError();
        observer.next(false);
        observer.complete();
      });
    });

  }

  private renderReport(data: any, task: ReportTask, filename: string) {
    filename = filename || 'report';
    let contentType: string;
    let fileExtension: string;
    switch (task) {
      case ReportTask.PDF:
        contentType = 'application/pdf';
        fileExtension = '.pdf';
        break;
      case ReportTask.XLS:
        contentType = 'application/vnd.ms-excel';
        fileExtension = '.xlsx';
        break;
    }
    const blob = this.b64toBlob(data, contentType);

    let blobUrl = URL.createObjectURL(blob);
    if (task === ReportTask.PDF) {
      blobUrl += '#view=fit';
    }
    const safeBlobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);


    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      // super special and custom IE stuff
      window.navigator.msSaveOrOpenBlob(blob, filename + fileExtension);
    } else if (task === ReportTask.PDF) {
      // open in dialog
      this.dialog.open(PdfViewerComponent,
        {
          data: safeBlobUrl,
          width: '80%',
          height: '80%',
          panelClass: 'pdfPanel'
        });
    } else {
      FileSaver.saveAs(blob, filename + fileExtension);
    }

  }

  private renderReportMobile(data: any, task: ReportTask, filename: string) {
    filename = filename || 'report';
    let contentType: string;
    let fileExtension: string;
    switch (task) {
      case ReportTask.PDF:
        contentType = 'application/pdf';
        fileExtension = '.pdf';
        break;
      case ReportTask.XLS:
        contentType = 'application/vnd.ms-excel';
        fileExtension = '.xlsx';
        break;
    }
    const blob = this.b64toBlob(data, contentType);
    FileSaver.saveAs(blob, filename + fileExtension);
  }

  private b64toBlob(b64Data: string, contentType: string, sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  private showDialogError() {
    this.dialogService.openConfirmDialog(
      this.translateService.translateFirstLetterUpperCase('LBLPANEL_ERROR_HTTP'),
      this.translateService.translateFirstLetterUpperCase('LBLPANEL_TIMEOUT_REQUEST'),
      this.translateService.translateFirstLetterUpperCase('LBLPANEL_CONFIRM'));
  }

}
